import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CreditCard, Coins, Crown } from "lucide-react";

interface BookingMethodsSelectProps {
  value: string[];
  onChange: (methods: string[]) => void;
}

const BOOKING_METHODS = [
  { id: "stripe", label: "Card Payment", icon: CreditCard, description: "Pay via Stripe" },
  { id: "credits", label: "Credits", icon: Coins, description: "Use Nimi credits" },
  { id: "unlimited", label: "Unlimited", icon: Crown, description: "For unlimited members" },
];

const BookingMethodsSelect = ({ value, onChange }: BookingMethodsSelectProps) => {
  const methods = value ?? ['stripe', 'credits', 'unlimited'];

  const handleToggle = (methodId: string, checked: boolean) => {
    if (checked) {
      onChange([...methods, methodId]);
    } else {
      // Don't allow deselecting if it's the last one
      if (methods.length <= 1) return;
      onChange(methods.filter((m) => m !== methodId));
    }
  };

  return (
    <div className="space-y-3">
      <Label>Allowed Booking Methods</Label>
      <p className="text-sm text-muted-foreground">
        Select which payment methods are available for this event. At least one must be selected.
      </p>
      <div className="space-y-2">
        {BOOKING_METHODS.map((method) => {
          const Icon = method.icon;
          const isChecked = methods.includes(method.id);
          
          return (
            <label
              key={method.id}
              htmlFor={`method-${method.id}`}
              className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                isChecked ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"
              }`}
            >
              <Checkbox
                id={`method-${method.id}`}
                checked={isChecked}
                onCheckedChange={(checked) => handleToggle(method.id, !!checked)}
              />
              <Icon className="h-4 w-4 text-muted-foreground" />
              <div className="flex-1">
                <span className="text-sm font-medium">
                  {method.label}
                </span>
                <p className="text-xs text-muted-foreground">{method.description}</p>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export default BookingMethodsSelect;
